import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/jobsiq/Navbar";
import Footer from "../components/jobsiq/Footer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MapPin, Briefcase, CurrencyInr, Clock, ArrowLeft, BookmarkSimple, Bookmark } from "@phosphor-icons/react";
import { formatSalaryINR } from "../lib/format";

export default function JobDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const [job, setJob] = useState(null);

    useEffect(() => {
        api.get(`/jobs/${id}`).then(({ data }) => setJob(data)).catch(() => setJob(null));
    }, [id]);

    const apply = async () => {
        if (!user) {
            nav("/login");
            return;
        }
        if (user.role !== "seeker") {
            toast.error("Only job seekers can apply");
            return;
        }
        try {
            await api.post(`/applications/${id}`);
            toast.success("Application submitted. AI match scored.");
            nav("/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Apply failed");
        }
    };

    const toggleSave = async () => {
        if (!user) {
            nav("/login");
            return;
        }
        if (user.role !== "seeker") return;
        try {
            if (job.is_saved) {
                await api.delete(`/saved-jobs/${job.job_id}`);
            } else {
                await api.post(`/saved-jobs/${job.job_id}`);
            }
            setJob({ ...job, is_saved: !job.is_saved });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed");
        }
    };

    if (!job) return <div className="min-h-screen"><Navbar /><div className="p-16 text-center text-slate-500">Loading job…</div></div>;

    const salary = formatSalaryINR(job.salary_min, job.salary_max);

    return (
        <div data-testid="job-detail-page">
            <Navbar />
            <section className="max-w-[1100px] mx-auto px-6 py-10">
                <Link to="/jobs" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-950">
                    <ArrowLeft size={14} /> All jobs
                </Link>
                <div className="mt-6 border border-slate-200 p-8">
                    <div className="font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                        {job.company_name}
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-tight" data-testid="job-detail-title">
                        {job.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-5 mt-5 text-sm text-slate-600">
                        <span className="inline-flex items-center gap-1.5">
                            <MapPin size={16} /> {job.location}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Briefcase size={16} /> {job.job_type}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <CurrencyInr size={16} /> {salary}
                        </span>
                        {job.experience_required ? (
                            <span className="inline-flex items-center gap-1.5">
                                <Clock size={16} /> {job.experience_required}+ yrs
                            </span>
                        ) : null}
                    </div>

                    {job.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-5">
                            {job.skills.map((s) => (
                                <span key={s} className="text-xs px-2 py-1 border border-slate-300 font-mono-plex">
                                    {s}
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-10 mt-10">
                        <div>
                            <h3 className="font-display text-xl">About the role</h3>
                            <p className="mt-3 text-slate-700 whitespace-pre-line leading-relaxed">{job.description}</p>
                        </div>
                        <div>
                            <h3 className="font-display text-xl">Requirements</h3>
                            <p className="mt-3 text-slate-700 whitespace-pre-line leading-relaxed">{job.requirements}</p>
                        </div>
                    </div>

                    <div className="mt-10 border-t border-slate-200 pt-6 flex items-center justify-between gap-4 flex-wrap">
                        <div className="text-xs font-mono-plex uppercase tracking-widest text-slate-500">
                            AI match scoring runs the moment you apply.
                        </div>
                        <div className="flex items-center gap-2">
                            {user?.role === "seeker" && (
                                <button
                                    data-testid="save-btn"
                                    onClick={toggleSave}
                                    className={`px-4 py-3 inline-flex items-center gap-1.5 border ${
                                        job.is_saved
                                            ? "bg-blue-50 border-blue-600 text-blue-700"
                                            : "border-slate-300 hover:border-slate-950"
                                    }`}
                                >
                                    {job.is_saved ? <BookmarkSimple size={16} weight="fill" /> : <Bookmark size={16} />}
                                    {job.is_saved ? "Saved" : "Save job"}
                                </button>
                            )}
                            <button
                                data-testid="apply-btn"
                                onClick={apply}
                                className="bg-blue-600 hover:bg-slate-950 text-white px-6 py-3"
                            >
                                Apply with AI match
                            </button>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
